/**
 * The feedback context allowlist (SA-1304): what a Help & feedback report may carry about where
 * the person was and what they did. The browser cleans every event through it; SA-1303's backend
 * holds the same allowlist and refuses a payload that does not already conform.
 *
 * ALLOWLIST is the data of shared/feedback-context-allowlist.json, pinned deep-equal by
 * __tests__/allowlist.test.ts (runtime code cannot read shared/: the Docker frontend build copies
 * only frontend/). Change the three copies together.
 */

export type FieldSpec =
  | { type: 'string'; max: number; scrub?: boolean; pattern?: string }
  | { type: 'int'; min: number; max: number }
  | { type: 'bool' }
  | { type: 'enum'; values: readonly string[] }
  | { type: 'string[]'; max: number; item_max: number; scrub?: boolean; item_pattern?: string }
  | { type: 'query' }

export interface KindSpec {
  required: readonly string[]
  keys: Record<string, FieldSpec>
}

export interface ScrubRule {
  name: string
  pattern: string
  replacement: string
}

export interface Allowlist {
  version: number
  ring: { max_events: number; max_age_ms: number }
  slug_pattern: string
  id_pattern: string
  common: Record<string, FieldSpec>
  kinds: Record<EventKind, KindSpec>
  query_keys: readonly string[]
  environment: Record<string, FieldSpec>
  scrub: readonly ScrubRule[]
}

export type EventKind = 'nav' | 'click' | 'change' | 'api' | 'error' | 'tour'

export const ALLOWLIST: Allowlist = {
  "version": 1,
  "ring": {
    "max_events": 50,
    "max_age_ms": 600000
  },
  "slug_pattern": "^[a-z][a-z0-9_]{0,63}$",
  "id_pattern": "^[A-Za-z0-9_.:-]{1,64}$",
  "common": {
    "at": {
      "type": "int",
      "min": 0,
      "max": 9999999999999
    }
  },
  "kinds": {
    "nav": {
      "required": [
        "path"
      ],
      "keys": {
        "path": {
          "type": "string",
          "max": 200,
          "scrub": false,
          "pattern": "^/[A-Za-z0-9_./:-]*$"
        },
        "params": {
          "type": "string[]",
          "max": 8,
          "item_max": 64,
          "scrub": false,
          "item_pattern": "id"
        },
        "query": {
          "type": "query"
        },
        "title": {
          "type": "string",
          "max": 120
        }
      }
    },
    "click": {
      "required": [
        "name"
      ],
      "keys": {
        "name": {
          "type": "string",
          "max": 80
        },
        "role": {
          "type": "enum",
          "values": [
            "button",
            "link",
            "tab",
            "menuitem",
            "option",
            "checkbox",
            "switch"
          ]
        }
      }
    },
    "change": {
      "required": [
        "field"
      ],
      "keys": {
        "field": {
          "type": "string",
          "max": 80
        }
      }
    },
    "api": {
      "required": [
        "method",
        "path",
        "status"
      ],
      "keys": {
        "method": {
          "type": "enum",
          "values": [
            "GET",
            "POST",
            "PUT",
            "PATCH",
            "DELETE"
          ]
        },
        "path": {
          "type": "string",
          "max": 200,
          "scrub": false,
          "pattern": "^/[A-Za-z0-9_./:-]*$"
        },
        "status": {
          "type": "int",
          "min": 0,
          "max": 599
        },
        "ms": {
          "type": "int",
          "min": 0,
          "max": 600000
        },
        "code": {
          "type": "string",
          "max": 64,
          "scrub": false,
          "pattern": "slug"
        }
      }
    },
    "error": {
      "required": [
        "source",
        "message"
      ],
      "keys": {
        "source": {
          "type": "enum",
          "values": [
            "window",
            "rejection",
            "console",
            "boundary",
            "toast",
            "refusal"
          ]
        },
        "message": {
          "type": "string",
          "max": 300
        },
        "code": {
          "type": "string",
          "max": 64,
          "scrub": false,
          "pattern": "slug"
        }
      }
    },
    "tour": {
      "required": [
        "action",
        "tour"
      ],
      "keys": {
        "action": {
          "type": "enum",
          "values": [
            "start",
            "step",
            "missing",
            "exit",
            "done"
          ]
        },
        "tour": {
          "type": "string",
          "max": 64,
          "scrub": false,
          "pattern": "slug"
        },
        "step": {
          "type": "int",
          "min": 0,
          "max": 20
        }
      }
    }
  },
  "query_keys": [
    "order",
    "step",
    "panel",
    "view"
  ],
  "environment": {
    "features": {
      "type": "string[]",
      "max": 120,
      "item_max": 64,
      "scrub": false,
      "item_pattern": "slug"
    },
    "view_as": {
      "type": "bool"
    },
    "viewport_w": {
      "type": "int",
      "min": 0,
      "max": 20000
    },
    "viewport_h": {
      "type": "int",
      "min": 0,
      "max": 20000
    },
    "theme": {
      "type": "enum",
      "values": [
        "light",
        "dark"
      ]
    },
    "browser": {
      "type": "enum",
      "values": [
        "chrome",
        "edge",
        "firefox",
        "safari",
        "other"
      ]
    },
    "browser_version": {
      "type": "int",
      "min": 0,
      "max": 999
    },
    "platform": {
      "type": "enum",
      "values": [
        "macos",
        "windows",
        "ios",
        "android",
        "linux",
        "other"
      ]
    },
    "build": {
      "type": "string",
      "max": 64,
      "scrub": false,
      "pattern": "id"
    }
  },
  "scrub": [
    {
      "name": "url_query",
      "pattern": "((?:https?://|/)[^\\s?#]*)[?#][^\\s]+",
      "replacement": "$1"
    },
    {
      "name": "jwt",
      "pattern": "eyJ[A-Za-z0-9_-]+\\.[A-Za-z0-9_-]+(?:\\.[A-Za-z0-9_-]+)?",
      "replacement": "[secret]"
    },
    {
      "name": "bearer",
      "pattern": "[Bb][Ee][Aa][Rr][Ee][Rr]\\s+[A-Za-z0-9._~+/=-]+",
      "replacement": "[secret]"
    },
    {
      "name": "api_key",
      "pattern": "\\b(?:eklive|ekdev)_[A-Za-z0-9_-]+|\\bsk-[A-Za-z0-9_-]{8,}",
      "replacement": "[secret]"
    },
    {
      "name": "email",
      "pattern": "[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\\.[A-Za-z]{2,}",
      "replacement": "[email]"
    },
    {
      "name": "long_run",
      "pattern": "(?=[A-Za-z0-9+/_-]*[0-9])[A-Za-z0-9+/_-]{24,}={0,2}",
      "replacement": "[secret]"
    },
    {
      "name": "money_symbol",
      "pattern": "[$€£¥]\\s?\\d(?:[\\d.,]*\\d)?|\\d(?:[\\d.,]*\\d)?\\s?[$€£¥]",
      "replacement": "[amount]"
    },
    {
      "name": "money_code",
      "pattern": "\\b(?:USD|EUR|GBP|MXN|CHF|JPY|AED|CAD|AUD|HKD|CNY|SGD|BGN)\\s?\\d(?:[\\d.,]*\\d)?|\\d(?:[\\d.,]*\\d)?\\s?(?:USD|EUR|GBP|MXN|CHF|JPY|AED|CAD|AUD|HKD|CNY|SGD|BGN)\\b",
      "replacement": "[amount]"
    },
    {
      "name": "phone",
      "pattern": "\\+\\d[\\d\\s().-]{6,}\\d|\\(\\d{3}\\)\\s?\\d{3}[-.\\s]\\d{4}",
      "replacement": "[number]"
    },
    {
      "name": "digits",
      "pattern": "\\d{6,}",
      "replacement": "[number]"
    }
  ]
}

export const EVENT_KINDS = Object.keys(ALLOWLIST.kinds) as EventKind[]

const SCRUBBERS = ALLOWLIST.scrub.map((rule) => ({
  re: new RegExp(rule.pattern, 'g'),
  replacement: rule.replacement,
}))

const NAMED_PATTERNS: Record<string, RegExp> = {
  slug: new RegExp(ALLOWLIST.slug_pattern),
  id: new RegExp(ALLOWLIST.id_pattern),
}

function patternOf(name: string | undefined): RegExp | null {
  if (!name) return null
  return NAMED_PATTERNS[name] ?? new RegExp(name)
}

/** Replace anything that looks private in free text, in the allowlist's order. */
export function scrub(text: string): string {
  let out = text
  for (const { re, replacement } of SCRUBBERS) out = out.replace(re, replacement)
  return out
}

/** Cut to a number of characters (code points, so a surrogate pair is never split). */
function cut(text: string, max: number): string {
  const chars = Array.from(text)
  return chars.length > max ? chars.slice(0, max).join('') : text
}

function cleanString(value: unknown, max: number, doScrub: boolean, pattern: RegExp | null): string | undefined {
  if (typeof value !== 'string') return undefined
  const collapsed = doScrub ? scrub(value.replace(/\s+/g, ' ').trim()) : value
  const out = cut(collapsed, max)
  if (out === '') return undefined
  if (pattern && !pattern.test(out)) return undefined
  return out
}

/** Clean one value against its spec; undefined means "drop the key". */
export function cleanValue(spec: FieldSpec, value: unknown): unknown {
  switch (spec.type) {
    case 'string':
      return cleanString(value, spec.max, spec.scrub !== false, patternOf(spec.pattern))
    case 'int': {
      if (typeof value !== 'number' || !Number.isFinite(value)) return undefined
      const n = Math.round(value)
      return n < spec.min || n > spec.max ? undefined : n
    }
    case 'bool':
      return typeof value === 'boolean' ? value : undefined
    case 'enum':
      return typeof value === 'string' && spec.values.includes(value) ? value : undefined
    case 'string[]': {
      if (!Array.isArray(value)) return undefined
      const pattern = patternOf(spec.item_pattern)
      const items: string[] = []
      for (const item of value) {
        if (items.length >= spec.max) break
        const cleaned = cleanString(item, spec.item_max, spec.scrub !== false, pattern)
        if (cleaned !== undefined) items.push(cleaned)
      }
      return items
    }
    case 'query': {
      if (value === null || typeof value !== 'object' || Array.isArray(value)) return undefined
      const id = NAMED_PATTERNS.id
      const out: Record<string, string> = {}
      for (const key of ALLOWLIST.query_keys) {
        const cleaned = cleanString((value as Record<string, unknown>)[key], 64, false, id)
        if (cleaned !== undefined) out[key] = cleaned
      }
      return Object.keys(out).length > 0 ? out : undefined
    }
  }
}

function cleanFields(specs: Record<string, FieldSpec>, raw: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {}
  for (const [key, spec] of Object.entries(specs)) {
    if (!(key in raw)) continue
    const cleaned = cleanValue(spec, raw[key])
    if (cleaned !== undefined) out[key] = cleaned
  }
  return out
}

export type FeedbackEvent = { kind: EventKind; at: number } & Record<string, unknown>

/**
 * Clean one event: keys the allowlist does not name are dropped, values of the wrong type are
 * dropped, strings are scrubbed then cut. Null when the kind is unknown or a required key is gone.
 */
export function cleanEvent(kind: string, raw: Record<string, unknown>): FeedbackEvent | null {
  const spec = (ALLOWLIST.kinds as Record<string, KindSpec>)[kind]
  if (!spec) return null
  const common = cleanFields(ALLOWLIST.common, raw)
  if (typeof common.at !== 'number') return null
  const fields = cleanFields(spec.keys, raw)
  for (const key of spec.required) if (!(key in fields)) return null
  return { kind: kind as EventKind, at: common.at, ...fields }
}

/** Clean the environment snapshot; anything not named is dropped. */
export function cleanEnvironment(raw: Record<string, unknown>): Record<string, unknown> {
  return cleanFields(ALLOWLIST.environment, raw)
}

/** The allowed query keys of a location.search string, values that look like ids only. */
export function cleanQuery(search: string): Record<string, string> | undefined {
  const params = new URLSearchParams(search)
  const raw: Record<string, string> = {}
  for (const key of ALLOWLIST.query_keys) {
    const value = params.get(key)
    if (value !== null) raw[key] = value
  }
  return cleanValue({ type: 'query' }, raw) as Record<string, string> | undefined
}
